import {
  minutes,
  money,
  type ISODateTime,
  type Minutes,
  type Money,
} from '../domain/branded.js';
import type { AccrualType } from '../domain/task.js';
import type { TimephasedCostSeries, TimephasedGranularity } from '../domain/timephased.js';
import type { ResolvedCalendar } from './calendar-engine.js';
import { workingMinutesBetween } from './calendar-engine.js';
import {
  civilFromDays,
  daysFromCivil,
  formatDateTime,
  normalize,
  parseDateTime,
  weekDayOf,
  type DayMinute,
} from './calendar-time.js';

/**
 * 逐時段成本輔助模組。
 *
 * 週期邊界一律用民用日期（Howard Hinnant 演算法, calendar-time.ts）計算，
 * 不使用 `Date`。工作分鐘數一律呼叫 calendar-engine 的 `workingMinutesBetween`。
 */

/** 對齊到週期邊界（<= at 的最近一個邊界）。 */
function alignPeriodStart(at: DayMinute, granularity: TimephasedGranularity): DayMinute {
  switch (granularity) {
    case 'Minutes':
      return at;
    case 'Hours':
      return { day: at.day, minute: Math.floor(at.minute / 60) * 60 };
    case 'Days':
      return { day: at.day, minute: 0 };
    case 'Weeks': {
      // 週一為週期起點；weekDayOf: 0=週日..6=週六。
      const sinceMonday = (weekDayOf(at.day) + 6) % 7;
      return { day: at.day - sinceMonday, minute: 0 };
    }
    case 'Months': {
      const civil = civilFromDays(at.day);
      return { day: daysFromCivil(civil.year, civil.month, 1), minute: 0 };
    }
    case 'Quarters': {
      const civil = civilFromDays(at.day);
      const quarterMonth = civil.month - ((civil.month - 1) % 3);
      return { day: daysFromCivil(civil.year, quarterMonth, 1), minute: 0 };
    }
    case 'Years': {
      const civil = civilFromDays(at.day);
      return { day: daysFromCivil(civil.year, 1, 1), minute: 0 };
    }
  }
}

/** 週期邊界之後的下一個邊界。 */
function nextPeriodStart(at: DayMinute, granularity: TimephasedGranularity): DayMinute {
  switch (granularity) {
    case 'Minutes':
      return normalize(at.day, at.minute + 1);
    case 'Hours':
      return normalize(at.day, at.minute + 60);
    case 'Days':
      return { day: at.day + 1, minute: 0 };
    case 'Weeks':
      return { day: at.day + 7, minute: 0 };
    case 'Months': {
      const civil = civilFromDays(at.day);
      const nextMonth = civil.month === 12 ? 1 : civil.month + 1;
      const nextYear = civil.month === 12 ? civil.year + 1 : civil.year;
      return { day: daysFromCivil(nextYear, nextMonth, 1), minute: 0 };
    }
    case 'Quarters': {
      const civil = civilFromDays(at.day);
      const quarterMonth = civil.month - ((civil.month - 1) % 3);
      const nextQuarterMonth = quarterMonth + 3 > 12 ? quarterMonth + 3 - 12 : quarterMonth + 3;
      const nextYear = quarterMonth + 3 > 12 ? civil.year + 1 : civil.year;
      return { day: daysFromCivil(nextYear, nextQuarterMonth, 1), minute: 0 };
    }
    case 'Years': {
      const civil = civilFromDays(at.day);
      return { day: daysFromCivil(civil.year + 1, 1, 1), minute: 0 };
    }
  }
}

interface RawSlice {
  readonly start: DayMinute;
  readonly end: DayMinute;
}

const cmp = (a: DayMinute, b: DayMinute): number => (a.day !== b.day ? a.day - b.day : a.minute - b.minute);

/** 產生 [from, to) 之間依 granularity 切分、且落在該範圍內的週期切片。 */
function generateSlices(
  from: ISODateTime,
  to: ISODateTime,
  granularity: TimephasedGranularity,
): RawSlice[] {
  const start = parseDateTime(from);
  const end = parseDateTime(to);
  if (cmp(start, end) >= 0) return [];

  const slices: RawSlice[] = [];
  let cursor = start;
  const MAX_SLICES = 100_000;
  for (let guard = 0; guard < MAX_SLICES; guard += 1) {
    const periodStart = alignPeriodStart(cursor, granularity);
    const periodEnd = nextPeriodStart(periodStart, granularity);
    const sliceStart = cmp(periodStart, cursor) > 0 ? periodStart : cursor;
    const sliceEnd = cmp(periodEnd, end) < 0 ? periodEnd : end;
    slices.push({ start: sliceStart, end: sliceEnd });
    if (cmp(sliceEnd, end) >= 0) break;
    cursor = sliceEnd;
  }
  return slices;
}

const toISO = (dm: DayMinute): ISODateTime => formatDateTime(dm.day, dm.minute);

/**
 * 依 `accrueAt` 將 `totalCost` 分佈到 [start, finish) 之間的逐時段切片。
 * - Start：全額計在涵蓋 `start` 的第一個切片。
 * - End：全額計在涵蓋 `finish`（不含端點，取最後一個切片）的切片。
 * - Prorated：依各切片內的工作分鐘數佔總工作分鐘數的比例分攤；
 *   若總工作分鐘數為 0（例：里程碑），全額落在唯一切片（或無切片時回傳空陣列）。
 */
export function distributeCost(params: {
  readonly start: ISODateTime;
  readonly finish: ISODateTime;
  readonly totalCost: Money;
  readonly accrueAt: AccrualType;
  readonly granularity: TimephasedGranularity;
  readonly calendar: ResolvedCalendar;
}): TimephasedCostSeries {
  const { start, finish, totalCost, accrueAt, granularity, calendar } = params;
  const slices = generateSlices(start, finish, granularity);
  if (slices.length === 0) {
    // 里程碑或零長度區間：整筆成本落在單一瞬間切片。
    if ((totalCost as number) === 0) return [];
    return [{ kind: 'Cost', start, end: finish, valueCost: totalCost }];
  }

  if (accrueAt === 'Start') {
    const first = slices[0];
    if (first === undefined) return [];
    return slices.map((slice, i) => ({
      kind: 'Cost' as const,
      start: toISO(slice.start),
      end: toISO(slice.end),
      valueCost: i === 0 ? totalCost : money(0),
    }));
  }

  if (accrueAt === 'End') {
    const lastIndex = slices.length - 1;
    return slices.map((slice, i) => ({
      kind: 'Cost' as const,
      start: toISO(slice.start),
      end: toISO(slice.end),
      valueCost: i === lastIndex ? totalCost : money(0),
    }));
  }

  // Prorated：依工作分鐘數比例分攤（本身就是「工期」的度量，因為工作分鐘數
  // 是把日曆非工作時段排除後的工期）。
  const sliceMinutes = slices.map((slice) =>
    Math.abs(workingMinutesBetween(calendar, toISO(slice.start), toISO(slice.end)) as number),
  );
  const totalMinutes = sliceMinutes.reduce((sum, m) => sum + m, 0);

  if (totalMinutes === 0) {
    // 沒有工作分鐘（例：整段落在非工作時間）：按切片數平均分攤，確保總和仍等於 totalCost。
    const each = (totalCost as number) / slices.length;
    let allocated = 0;
    return slices.map((slice, i) => {
      const value = i === slices.length - 1 ? (totalCost as number) - allocated : each;
      allocated += value;
      return {
        kind: 'Cost' as const,
        start: toISO(slice.start),
        end: toISO(slice.end),
        valueCost: money(value),
      };
    });
  }

  let allocated = 0;
  return slices.map((slice, i) => {
    const isLast = i === slices.length - 1;
    const raw = (totalCost as number) * (sliceMinutes[i] ?? 0) / totalMinutes;
    // 最後一個切片吃下四捨五入殘值，確保切片加總精確等於 totalCost。
    const value = isLast ? (totalCost as number) - allocated : raw;
    allocated += value;
    return {
      kind: 'Cost' as const,
      start: toISO(slice.start),
      end: toISO(slice.end),
      valueCost: money(value),
    };
  });
}

/** 累加多個 TimephasedCostSeries 為單一「合併切片」序列（依起訖時間直接串接，不重新切期）。 */
export function mergeCostSeries(series: readonly TimephasedCostSeries[]): TimephasedCostSeries {
  const all = series.flat();
  return [...all].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
}

/** 計算 TimephasedCostSeries 在 `asOf`（不含）之前的累積金額。 */
export function cumulativeCostBefore(series: TimephasedCostSeries, asOf: ISODateTime): Money {
  let total = 0;
  for (const slice of series) {
    if (slice.start < asOf) {
      total += slice.valueCost as number;
    }
  }
  return money(total);
}

/** 供外部使用的工期常數，避免各處重複 import calendar-time 內部型別。 */
export const zeroMinutes: Minutes = minutes(0);
